<template>
  <Card v-if="item" class="cursor-move border-1 rounded-sm p-2 w-70 mb-0">
    <CardContent class="px-2">
      <router-link :to="`/contacts/${item.id}`" class="flex flex-col gap-2">
        <CandidateHeader
          :avatar="item.initials.toUpperCase()"
          :header="item.name"
          :subheader="item.applied_at ? 'Applied ' + formatShortDate(item.applied_at) : ''"
        />
        <div class="flex flex-wrap items-center gap-1">
          <CandidateLicenseBadge :license-status-label="item.license_status" />
          <CandidateReferredBadge v-if="item.is_referred" />
          <Badge v-if="item.application_count > 1" variant="gray">{{ item.application_count }} roles</Badge>
        </div>
        <CandidateInfo :email="item.email" :phone="item.phone" />
        <CandidateTags :tags="item.tags" />
      </router-link>
      <CandidateActionButtons :item="item" />
    </CardContent>
  </Card>
  <KanbanEmptyCard v-else message="No candidates" />

  <template v-if="hasTrigger && item">
    <ContactOnboard
      v-if="showContactOnboard"
      :contact="item"
      @handle-contact-onboard="emit('handle-update', 'update')"
      @handle-contact-onboard-cancel="emit('handle-update', 'revert', 'Reverting move to Onboarding')"
    />
    <ContactDisqualify
      v-if="showContactDisqualify"
      :contact="{ id: item.id, canReceiveSms: item.can_receive_sms }"
      @handle-contact-disqualify="emit('handle-update', 'update')"
      @handle-contact-disqualify-cancel="emit('handle-update', 'revert', 'Reverting move to Rejected')"
    />
    <MeetingStageSheet
      v-if="showMeetingStageSheet"
      :contact="contactDetail"
      :contact-stage="trigger!.column_id as ContactStage"
      :move-contact-stage="moveContactStage"
      @stage-changed="emit('handle-update', 'done')"
      @handle-cancel="emit('handle-update', 'revert', `Reverting move to ${trigger!.column_id}`)"
    />
  </template>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import ContactOnboard from '@/components/contact/ContactOnboard.vue';
import ContactDisqualify from '@/components/contact/ContactDisqualify/index.vue';
import MeetingStageSheet from '@/components/contact/MeetingStageSheet/index.vue';
import type { CandidateEntity } from '@/api/entity_types/recruiting/Candidate';
import type { ContactStage } from '@/api/entity_types/Contact';
import type { ContactDetail } from '@/components/contact/ContactDetail';
import { useMoveContactStage } from '@/components/contact/ContactStageActions/useMoveContactStage';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import CandidateActionButtons from '@/components/recruiting/Candidates/CandidateActionButtons.vue';
import CandidateHeader from '@/components/recruiting/Candidates/CandidateHeader.vue';
import CandidateLicenseBadge from '@/components/recruiting/Candidates/CandidateLicenseBadge.vue';
import CandidateReferredBadge from '@/components/recruiting/Candidates/CandidateReferredBadge.vue';
import CandidateInfo from '@/components/recruiting/Candidates/CandidateInfo.vue';
import CandidateTags from '@/components/recruiting/Candidates/CandidateTags.vue';
import { formatShortDate } from '../../../utilities/format.ts';
import KanbanEmptyCard from '@/components/shared/Kanban/KanbanEmptyCard.vue';

const { item, trigger } = defineProps<{
  item?: CandidateEntity;
  trigger?: { column_id: string | number; id: string | number };
}>();

const emit = defineEmits(['handle-update']);

const showContactOnboard = computed(() => trigger?.column_id === 'Onboarding');
const showContactDisqualify = computed(() => trigger?.column_id === 'Disqualified');
const showMeetingStageSheet = computed(
  () => trigger?.column_id === 'Phone Screen' || trigger?.column_id === 'Interview'
);

const contactDetail = computed<
  Pick<ContactDetail, 'id' | 'stage' | 'firstName' | 'lastName' | 'email' | 'phone' | 'canReceiveSms'>
>(() => {
  if (!item)
    return { id: 0, stage: 'Applied', firstName: '', lastName: '', email: '', phone: '', canReceiveSms: false };
  return {
    id: item.id,
    stage: item.stage_name,
    firstName: item.first_name,
    lastName: item.last_name,
    email: item.email,
    phone: item.phone,
    canReceiveSms: item.can_receive_sms,
  };
});

const contactRef = computed(() => ({ id: contactDetail.value.id, stage: contactDetail.value.stage }));
const moveContactStage = useMoveContactStage(contactRef);

const hasTrigger = computed((): boolean => {
  return item?.id === trigger?.id && hasColumnAction();
});

function hasColumnAction() {
  if (showContactOnboard.value || showContactDisqualify.value || showMeetingStageSheet.value) {
    return true;
  } else {
    emit('handle-update', 'update');
    return false;
  }
}
</script>
